import type { QualifyBand } from "./qualifyLead";

/**
 * W4 SIGNALS (B7, B15): prioritized replay by surprise.
 *
 * WHAT IT IS FOR. The learner may spend a paid model read on only so many
 * closed threads a week. This orders the losses worth reading first: the ones
 * whose outcome sat furthest from what the lead's band led anyone to expect.
 *
 * WHAT IT IS NOT. A ranking, never evidence. It decides which threads get the
 * paid read; every statistical test downstream uses EVERY thread, so the
 * order can never manufacture a result (design W4: "Priority only picks which
 * threads get the paid model read; evidence uses every thread").
 *
 * STRATIFIED. Taken round-robin across inquiry types, top K from each, so a
 * wave of one kind of inquiry cannot crowd the others out of the read.
 *
 * NOT MEASURED IS NOT ZERO. A thread with no measured band (U3 wrote no
 * `lead.scored` row), a band with fewer than `SURPRISE_MIN_BAND_N` prior rows,
 * or no outcome yet, has no
 * surprise to compute and is left out of the ranking. It is never given a
 * default rate.
 *
 * Pure: no clock, no I/O, no model.
 */

/** Where a closed thread ended up. `open` is not an outcome yet. */
export type ThreadOutcome = "won" | "deposit" | "consult_booked" | "lost" | "ghosted" | "open";

/**
 * One thread as the learner reads it: codes and flags only, no message text.
 * W2 builds these from the ledger; this file and `invisibleFailure.ts` take
 * them as given (the shape is the design's `ThreadOutcomeView`, named SurpriseThreadView here so it does not collide with the learner's view of the same name).
 */
export interface SurpriseThreadView {
  readonly threadId: string;
  readonly contactId: string;
  readonly outcome: ThreadOutcome;
  /** U3's `lead.scored` band by payload field; null when no row exists (not_measured). */
  readonly band: QualifyBand | null;
  /** U3's `raising` codes; empty when none or not measured. */
  readonly raising: readonly string[];
  /** A `consult.intent` row exists on the thread. */
  readonly consultIntent: boolean;
  /** The inquiry-type stratum, or `unknown`. */
  readonly inquiryType: string;
  /** Every gate was green on the FIRST pass of every draft (F-25), the AI-tell linter included. */
  readonly firstPassGatesGreen: boolean;
  /** Every draft the owner decided was approved unedited or with a cosmetic edit (F-05). */
  readonly approvedWithoutEdit: boolean;
  /**
   * How many drafts the owner decided on the thread. Zero makes the two booleans above vacuous
   * ("every draft" of none), so a thread with none can never be an invisible failure (review 1 M4).
   */
  readonly decidedDraftCount: number;
  /** ISO time the thread closed. */
  readonly closedAt: string;
}

/** Prior weeks' counts per band: closed threads and how many of them won. */
export type BandRates = Readonly<Partial<Record<QualifyBand, { readonly n: number; readonly won: number }>>>;

/**
 * Threads per inquiry type that the paid read may take in one batch.
 * AWAITING RATIFICATION (design W4: the per-stratum K; Kabir to rule).
 */
export const SURPRISE_PER_STRATUM_K = 10;

/**
 * The fewest prior closed threads a band may have before its base rate is read.
 * Below it a Laplace-smoothed rate is mostly the prior (n = 0 reads 0.5), and a
 * ranking built on it would be noise. AWAITING RATIFICATION (Kabir to rule).
 */
export const SURPRISE_MIN_BAND_N = 5;

/**
 * Where each outcome sits between a loss (0) and a win (1), the design's
 * outcome ordering: won, then deposit, then consult_booked, then lost and
 * ghosted. A deposit or a booked consult is real progress, never a loss.
 * `open` has no score. The two middle values are placeholders for the
 * ordering, AWAITING RATIFICATION (Kabir to rule); the ranking only decides
 * which threads get the paid read, never evidence.
 */
export const OUTCOME_SCORE: Readonly<Record<Exclude<ThreadOutcome, "open">, number>> = {
  won: 1,
  deposit: 0.75, // AWAITING RATIFICATION
  consult_booked: 0.5, // AWAITING RATIFICATION
  lost: 0,
  ghosted: 0,
};

/**
 * The band's win rate over prior weeks, Laplace smoothed: (won + 1) / (n + 2).
 * `not_measured` when the band has no prior row or fewer than
 * `SURPRISE_MIN_BAND_N`: no base rate is not a base rate of zero, and a
 * thin one is not a base rate of one half.
 */
export function bandBaseRate(rates: BandRates, band: QualifyBand | null): number | "not_measured" {
  if (band === null) return "not_measured";
  const r = Object.hasOwn(rates, band) ? rates[band] : undefined;
  if (r === undefined || !Number.isInteger(r.n) || !Number.isInteger(r.won) || r.n < 0 || r.won < 0 || r.won > r.n) return "not_measured";
  if (r.n < SURPRISE_MIN_BAND_N) return "not_measured";
  return (r.won + 1) / (r.n + 2);
}

/**
 * |outcome score - base rate|, the score from `OUTCOME_SCORE`. Null when the
 * thread is open or its band's base rate is not measured.
 */
export function surpriseOf(v: SurpriseThreadView, rates: BandRates): number | null {
  if (v.outcome === "open") return null;
  const base = bandBaseRate(rates, v.band);
  if (base === "not_measured") return null;
  return Math.abs(OUTCOME_SCORE[v.outcome] - base);
}

/**
 * The stratum a thread belongs to for the evidence half (design G5): inquiry
 * type, price band and season. The price band (D34) and season (D39) are not
 * built yet, so every thread is `no_amount` and the season is the close
 * month tag (`YYYY-MM`, `unknown` when the time is not an ISO date); a report
 * over these strata says underpowered. AWAITING RATIFICATION until D34/D39 land.
 */
export function stratumOf(v: SurpriseThreadView): { readonly inquiryType: string; readonly priceBand: "no_amount"; readonly season: string } {
  const m = /^(\d{4}-\d{2})-\d{2}/.exec(v.closedAt);
  return { inquiryType: v.inquiryType, priceBand: "no_amount", season: m?.[1] ?? "unknown" };
}

export interface RankedThread {
  readonly view: SurpriseThreadView;
  readonly surprise: number;
  readonly stratum: ReturnType<typeof stratumOf>;
}

/**
 * The threads worth a paid read first, in read order, each with its surprise
 * and its full stratum. Within an inquiry type, most surprising first (ties by
 * thread id); across types, round-robin in type-name order; at most
 * `perStratumK` from each type. Unrankable threads are absent. The input is
 * not changed.
 */
export function prioritizeWithStrata(
  views: readonly SurpriseThreadView[],
  rates: BandRates,
  perStratumK: number,
): RankedThread[] {
  if (!Number.isInteger(perStratumK) || perStratumK <= 0) {
    throw new Error(`prioritizeBySurprise: perStratumK must be a positive integer, got ${String(perStratumK)}`);
  }
  const strata = new Map<string, RankedThread[]>();
  for (const v of views) {
    const surprise = surpriseOf(v, rates);
    if (surprise === null) continue;
    const list = strata.get(v.inquiryType) ?? [];
    list.push({ view: v, surprise, stratum: stratumOf(v) });
    strata.set(v.inquiryType, list);
  }
  const queues = [...strata.keys()]
    .sort()
    .map((k) =>
      (strata.get(k) ?? [])
        .sort((a, b) => b.surprise - a.surprise || (a.view.threadId < b.view.threadId ? -1 : a.view.threadId > b.view.threadId ? 1 : 0))
        .slice(0, perStratumK),
    );
  const out: RankedThread[] = [];
  for (let rank = 0; rank < perStratumK; rank++) {
    for (const q of queues) {
      const next = q[rank];
      if (next !== undefined) out.push(next);
    }
  }
  return out;
}

/** `prioritizeWithStrata` as the design's plain list of views. */
export function prioritizeBySurprise(
  views: readonly SurpriseThreadView[],
  rates: BandRates,
  perStratumK: number,
): SurpriseThreadView[] {
  return prioritizeWithStrata(views, rates, perStratumK).map((r) => r.view);
}
